import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Table,
  Spinner,
} from "reactstrap";
import { useAccount } from "../../context/AccountContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";

// Fields that are more single-stock tuning knobs than portfolio-level settings —
// mirrors StrategyBuild.jsx's ADVANCED_SETTINGS always-hide list so the shared
// panel here doesn't surface anything the per-symbol search already decides
// (max_call_lots is the search variable itself, not a fixed shared input).
const ALWAYS_HIDE = [
  "tail_risk_pct", "allow_tail_risk", "rollout_mode",
  "opening_call_delta", "opening_put_delta", "opening_lots",
  "max_call_lots",
  "average_buy_price_to_call_strike_ratio", "cash_saving_mode",
  "min_pct_hedge", "max_pct_hedge",
];

// Portfolio Builder is budget-driven, not hedge-band-driven: for a brand-new
// position hedge% is mathematically identical to the call's own delta no
// matter how many lots are bought (numerator and denominator both scale with
// call_lots), so a fixed Minimum Hedge % is often unsatisfiable — and as the
// ladder builds up over multiple lots, legitimate multi-lot hedges can show
// hedge% figures far outside a "sane-looking" 0-100 band (see
// strategies/pyramid_hedging.py hedge_pct calc). These overrides make the
// min/max hedge% check effectively a no-op here; Income-First Mode is forced
// on so the separate Hedge Style/ratio check (which IS bypassed by CSM) never
// blocks either. Single-stock StrategyBuild and the account Settings page are
// unaffected — this only changes what Portfolio Builder sends per request.
const FORCED_OVERRIDES = {
  cash_saving_mode: true,
  min_pct_hedge: 0,
  max_pct_hedge: 100000,
};

const POLL_INTERVAL_MS = 1500;

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const parseSymbols = (text) =>
  [...new Set(
    text
      .split(/[\n,]/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
  )];

const PortfolioBuilder = () => {
  const { selectedAccount } = useAccount();
  const username = selectedAccount;

  const [symbolsText, setSymbolsText] = useState("");
  const [totalCapital, setTotalCapital] = useState("");
  const [reservePct, setReservePct] = useState(10);

  const [settingsSchema, setSettingsSchema] = useState(null);
  const [dynamicValues, setDynamicValues] = useState({});
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0, current_symbol: null });
  const [results, setResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [recomputingSymbol, setRecomputingSymbol] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const pollTimer = useRef(null);

  // ------------------------------------------------
  // FETCH SHARED SETTINGS (PYRAMID_HEDGING only)
  // ------------------------------------------------
  useEffect(() => {
    if (!username) return;
    axios.get("/strategy/settings", { params: { strategy_type: "PYRAMID_HEDGING", username } })
      .then((res) => {
        setSettingsSchema(res.data);
        const initial = {};
        Object.entries(res.data.variable_inputs || {}).forEach(([key, config]) => {
          initial[key] = config.default !== undefined ? config.default : "";
        });
        Object.entries(res.data.user_settings || {}).forEach(([key, config]) => {
          initial[key] = config.value !== undefined ? config.value : "";
        });
        Object.assign(initial, FORCED_OVERRIDES);
        setDynamicValues(initial);
      })
      .catch(() => setError("Failed to load strategy settings."));
  }, [username]);

  useEffect(() => {
    axios.get("/expiries", { params: { strategy_type: "PYRAMID_HEDGING" } })
      .then((res) => {
        const exp = res.data.expiries || [];
        setAvailableExpiries(exp);
        if (exp.length > 0) {
          setDynamicValues((prev) => ({ ...prev, expiry: prev.expiry || exp[0] }));
        }
      })
      .catch(() => setAvailableExpiries([]));
  }, []);

  // ------------------------------------------------
  // POLL JOB STATUS
  // ------------------------------------------------
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`/strategy/portfolio-plan/${jobId}`);
        setJobStatus(res.data.status);
        setProgress({
          completed: res.data.completed,
          total: res.data.total,
          current_symbol: res.data.current_symbol,
        });
        if (res.data.status === "COMPLETED" || res.data.status === "PARTIAL_FAILED") {
          const rows = res.data.results || [];
          setResults(rows);
          const sel = {};
          rows.forEach((r) => { sel[r.symbol] = r.status === "ok"; });
          setSelectedRows(sel);
        } else if (res.data.status === "FAILED") {
          setError(res.data.error || "Portfolio plan job failed.");
        } else {
          pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (err) {
        setError("Failed to fetch job status.");
      }
    };

    poll();
    return () => clearTimeout(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const buildSharedVariableInputs = () => ({ ...dynamicValues });

  const handleGeneratePlan = async () => {
    setError(null);
    setSaveMessage(null);
    setResults([]);
    setJobStatus(null);

    const symbols = parseSymbols(symbolsText);
    if (symbols.length === 0) {
      setError("Enter at least one stock symbol.");
      return;
    }
    if (!totalCapital || Number(totalCapital) <= 0) {
      setError("Enter total capital to allocate.");
      return;
    }
    if (!dynamicValues.expiry) {
      setError("Select an option expiry before generating a plan.");
      return;
    }

    try {
      const res = await axios.post("/strategy/portfolio-plan", {
        username,
        strategy_type: "PYRAMID_HEDGING",
        symbols,
        total_capital: Number(totalCapital),
        reserve_pct: Number(reservePct) || 0,
        variable_inputs: buildSharedVariableInputs(),
      });
      setJobId(res.data.job_id);
      setJobStatus(res.data.status);
      setProgress({ completed: 0, total: symbols.length, current_symbol: null });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start portfolio plan.");
    }
  };

  // ------------------------------------------------
  // ROW-LEVEL RECOMPUTE (edited budget) — reuses the same
  // job endpoint for a single symbol rather than a bespoke route.
  // ------------------------------------------------
  const recomputeRow = async (symbol, newBudget) => {
    if (!newBudget || Number(newBudget) <= 0) return;
    setRecomputingSymbol(symbol);
    try {
      const res = await axios.post("/strategy/portfolio-plan", {
        username,
        strategy_type: "PYRAMID_HEDGING",
        symbols: [symbol],
        total_capital: Number(newBudget),
        reserve_pct: 0,
        variable_inputs: buildSharedVariableInputs(),
      });
      const rowJobId = res.data.job_id;

      // Short-lived poll scoped to this one symbol.
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const statusRes = await axios.get(`/strategy/portfolio-plan/${rowJobId}`);
        if (["COMPLETED", "PARTIAL_FAILED", "FAILED"].includes(statusRes.data.status)) {
          const updated = (statusRes.data.results || [])[0];
          if (updated) {
            setResults((prev) => prev.map((r) => (r.symbol === symbol ? updated : r)));
          }
          break;
        }
      }
    } catch (err) {
      setError(`Failed to recompute ${symbol}.`);
    } finally {
      setRecomputingSymbol(null);
    }
  };

  const toggleRow = (symbol) => setSelectedRows((prev) => ({ ...prev, [symbol]: !prev[symbol] }));

  const handleSaveAll = async () => {
    setError(null);
    setSaveMessage(null);
    const strategiesToSave = results
      .filter((r) => selectedRows[r.symbol] && r.status === "ok" && r.build_result)
      .map((r) => ({ symbolname: r.symbol, strategy_state: r.build_result }));

    if (strategiesToSave.length === 0) {
      setError("No successful, selected strategies to save.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await axios.post("/strategy/save", {
        username,
        strategy_type: "PYRAMID_HEDGING",
        strategies: strategiesToSave,
      });
      setSaveMessage(
        `Saved ${res.data.saved_count} strategy(ies) as INACTIVE. ` +
        `Go to Manage Strategies to review and activate.`
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save strategies.");
    } finally {
      setIsSaving(false);
    }
  };

  const totalBudget = results.reduce((sum, r) => sum + (r.budget || 0), 0);
  const totalProjected = results.reduce((sum, r) => sum + (r.projected_capital || 0), 0);
  const jobRunning = jobStatus === "CREATED" || jobStatus === "RUNNING";

  return (
    <>
      <PageTitleAlt2
        heading="Portfolio Builder"
        subheading="Allocate capital across many stocks at once and build Pyramid Hedging strategies in bulk"
        icon="pe-7s-network icon-gradient bg-happy-itmeo"
      />

      <Card className="mt-3">
        <CardHeader>Symbols &amp; Capital</CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}
          {saveMessage && <Alert color="success">{saveMessage}</Alert>}

          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Symbols (one per line, or comma-separated)</Label>
                  <Input
                    type="textarea"
                    rows={6}
                    placeholder={"AAPL\nMSFT\nNVDA\n..."}
                    value={symbolsText}
                    onChange={(e) => setSymbolsText(e.target.value)}
                    disabled={jobRunning}
                  />
                  <small className="text-muted">
                    {parseSymbols(symbolsText).length} symbol(s)
                  </small>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label>Total Capital ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 1000000"
                    value={totalCapital}
                    onChange={(e) => setTotalCapital(e.target.value)}
                    disabled={jobRunning}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Reserve %: {reservePct}%</Label>
                  <Input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={reservePct}
                    onChange={(e) => setReservePct(e.target.value)}
                    disabled={jobRunning}
                  />
                  <small className="text-muted">Capital held back as a buffer, not allocated.</small>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label>Expiry</Label>
                  <Input
                    type="select"
                    value={dynamicValues.expiry || ""}
                    onChange={(e) => setDynamicValues({ ...dynamicValues, expiry: e.target.value })}
                    disabled={jobRunning}
                  >
                    <option value="">— Select expiry —</option>
                    {availableExpiries.map((exp) => (
                      <option key={exp} value={exp}>
                        {new Date(exp + "T00:00:00").toLocaleDateString("en-US", {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {settingsSchema?.user_settings && (
              <Card className="mt-2" style={{ background: "#fafbfc" }}>
                <CardHeader
                  style={{ cursor: "pointer" }}
                  className="d-flex justify-content-between"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                >
                  <span>Shared Advanced Settings (applied to every symbol)</span>
                  <span>{advancedOpen ? "▲" : "▼"}</span>
                </CardHeader>
                {advancedOpen && (
                  <CardBody>
                    <Row>
                      {Object.entries(settingsSchema.user_settings)
                        .filter(([key]) => !ALWAYS_HIDE.includes(key))
                        .map(([key, config]) => (
                          <Col md={4} key={key}>
                            <FormGroup>
                              <Label>{config.title}</Label>
                              {typeof config.value === "boolean" ? (
                                <Input
                                  type="select"
                                  value={String(dynamicValues[key] ?? false)}
                                  onChange={(e) =>
                                    setDynamicValues({ ...dynamicValues, [key]: e.target.value === "true" })
                                  }
                                  disabled={jobRunning}
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Input>
                              ) : typeof config.value === "string" ? (
                                <Input
                                  type="text"
                                  value={dynamicValues[key] || ""}
                                  onChange={(e) => setDynamicValues({ ...dynamicValues, [key]: e.target.value })}
                                  disabled={jobRunning}
                                />
                              ) : (
                                <Input
                                  type="number"
                                  value={dynamicValues[key] ?? ""}
                                  onChange={(e) => setDynamicValues({ ...dynamicValues, [key]: e.target.value })}
                                  disabled={jobRunning}
                                />
                              )}
                              <small className="text-muted">{config.detail}</small>
                            </FormGroup>
                          </Col>
                        ))}
                    </Row>
                    <small className="text-muted">
                      Maximum Call Lots is not shown here — it's the ceiling the allocator
                      searches downward from to fit each symbol's budget, not a fixed shared value.
                    </small>
                  </CardBody>
                )}
              </Card>
            )}

            <div className="mt-3">
              <Button color="primary" onClick={handleGeneratePlan} disabled={jobRunning}>
                {jobRunning ? <Spinner size="sm" /> : "Generate Plan"}
              </Button>
            </div>
          </Form>

          {jobRunning && (
            <div className="mt-3">
              <div className="d-flex justify-content-between" style={{ fontSize: 13, color: "#666" }}>
                <span>
                  {jobStatus === "CREATED" ? "Starting…" : `Building ${progress.current_symbol || "…"}`}
                </span>
                <span>{progress.completed} / {progress.total}</span>
              </div>
              <div style={{ background: "#eee", borderRadius: 4, height: 8, marginTop: 4 }}>
                <div
                  style={{
                    width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%`,
                    background: "#545cd8", height: 8, borderRadius: 4,
                    transition: "width 0.3s",
                  }}
                />
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {results.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="d-flex justify-content-between align-items-center">
            <span>Portfolio Plan</span>
            <span style={{ fontSize: 12, color: "#666" }}>
              Budget total: {fmtMoney(totalBudget)} · Projected capital: {fmtMoney(totalProjected)}
            </span>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: "auto" }}>
              <Table bordered hover responsive style={{ textAlign: "center", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>
                      <Input
                        type="checkbox"
                        checked={
                          results.some((r) => r.status === "ok") &&
                          results.filter((r) => r.status === "ok").every((r) => selectedRows[r.symbol])
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const next = {};
                          results.forEach((r) => { next[r.symbol] = checked && r.status === "ok"; });
                          setSelectedRows(next);
                        }}
                      />
                    </th>
                    <th>Symbol</th>
                    <th>Budget ($)</th>
                    <th>Achieved Lots</th>
                    <th>Projected Capital</th>
                    <th>Call / CSP</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <RowEditor
                      key={r.symbol}
                      row={r}
                      selected={!!selectedRows[r.symbol]}
                      onToggle={() => toggleRow(r.symbol)}
                      onRecompute={recomputeRow}
                      recomputing={recomputingSymbol === r.symbol}
                    />
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="mt-3">
              <Button color="success" onClick={handleSaveAll} disabled={isSaving}>
                {isSaving ? <Spinner size="sm" /> : "Save All Selected"}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
};

// Small isolated component so each row's budget-edit box has its own local
// state and only triggers a recompute on blur/Enter, not on every keystroke.
const RowEditor = ({ row, selected, onToggle, onRecompute, recomputing }) => {
  const [budgetInput, setBudgetInput] = useState(row.budget ?? "");

  useEffect(() => setBudgetInput(row.budget ?? ""), [row.budget]);

  const commit = () => {
    if (Number(budgetInput) !== row.budget) {
      onRecompute(row.symbol, budgetInput);
    }
  };

  return (
    <tr>
      <td>
        <Input type="checkbox" checked={selected} onChange={onToggle} disabled={row.status !== "ok"} />
      </td>
      <td style={{ fontWeight: 500 }}>{row.symbol}</td>
      <td>
        <Input
          type="number"
          bsSize="sm"
          value={budgetInput}
          onChange={(e) => setBudgetInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          disabled={recomputing}
          style={{ width: 110, display: "inline-block" }}
        />
      </td>
      <td>{recomputing ? <Spinner size="sm" /> : (row.achieved_lots ?? "—")}</td>
      <td>{recomputing ? "—" : fmtMoney(row.projected_capital)}</td>
      <td style={{ fontSize: 11 }}>
        {row.call_symbol && <div style={{ color: "#28a745" }}>{row.call_symbol}</div>}
        {row.csp_symbol && <div style={{ color: "#dc3545" }}>{row.csp_symbol}</div>}
      </td>
      <td>
        {row.status === "ok" ? (
          <span style={{ color: "#28a745" }}>✓ OK</span>
        ) : row.status === "budget_too_small" ? (
          <span title={row.warning} style={{ color: "#e67e22", cursor: "help" }}>⚠ Budget too small</span>
        ) : (
          <span
            title={[row.error, ...(row.suggestions || [])].filter(Boolean).join("\n")}
            style={{ color: "#dc3545", cursor: "help" }}
          >
            ✗ {row.error || "Failed"}
          </span>
        )}
      </td>
      <td></td>
    </tr>
  );
};

export default PortfolioBuilder;
